import { useEffect, useState, Component, type ReactNode } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query'
import { Layout } from '@/components/layout/Layout'
import { DashboardPage } from '@/pages/DashboardPage'
import { VerificationPage } from '@/pages/VerificationPage'
import { OrdersPage } from '@/pages/OrdersPage'
import { MastersPage } from '@/pages/MastersPage'
import { FinancePage } from '@/pages/FinancePage'
import { MasterProfilePage } from '@/pages/MasterProfilePage'
import { ClientProfilePage } from '@/pages/ClientProfilePage'
import { Toaster } from '@/components/ui/toaster'
import { LoadingScreen } from '@/components/LoadingScreen'
import { fetchUserProfile } from '@/lib/userApi'

// Error Boundary Component
class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean, error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex h-screen flex-col items-center justify-center p-4 text-center bg-slate-50">
          <h1 className="text-xl font-bold text-red-600 mb-2">Критическая ошибка</h1>
          <pre className="bg-white p-3 rounded-xl shadow-sm border text-xs overflow-auto max-w-full text-left text-slate-700">
            {this.state.error?.message}
          </pre>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-xl font-medium shadow-sm"
          >
            Перезагрузить
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
})

/**
 * Smart Router - redirects based on user role
 */
function SmartRouter() {
  const { data: profile, isLoading, error } = useQuery({
    queryKey: ['userProfile'],
    queryFn: fetchUserProfile,
  })

  if (isLoading) {
    return <LoadingScreen />
  }

  if (error || !profile) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 p-4 text-center bg-slate-50">
        <div className="text-red-600 font-bold text-lg">Ошибка доступа</div>
        <p className="text-sm text-slate-500">
          Не удалось определить профиль пользователя.
        </p>
        <button 
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-slate-200 rounded-xl text-sm"
        >
          Повторить
        </button>
      </div>
    )
  }

  // Redirect based on role
  // Note: We are inside basename="/admin", so:
  // "/" -> /admin/
  // "/master" -> /admin/master
  if (profile.role === 'DISPATCHER') return <Navigate to="/admin" replace />
  if (profile.role === 'MASTER') return <Navigate to="/master" replace />
  if (profile.role === 'CLIENT') return <Navigate to="/client" replace />

  // Fallback
  return <Navigate to="/client" replace />
}

function AppContent() {
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    try {
      const tg = window.Telegram?.WebApp
      if (tg) {
        tg.ready()
        tg.expand()
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ;(tg as any).enableClosingConfirmation()
      }
      setIsReady(true)
    } catch (e) {
      console.warn('Telegram WebApp is not available')
      setIsReady(true)
    }
  }, [])

  if (!isReady) return null

  return (
    <BrowserRouter>
      <Routes>
        {/* Entry point check */}
        <Route path="/check" element={<SmartRouter />} />
        
        {/* Admin/Dispatcher routes */}
        <Route path="/admin" element={<Layout />}>
          <Route index element={<DashboardPage />} />
          <Route path="verification" element={<VerificationPage />} />
          <Route path="orders" element={<OrdersPage />} />
          <Route path="masters" element={<MastersPage />} />
          <Route path="finance" element={<FinancePage />} />
        </Route>

        {/* Master & Client routes (Siblings inside /admin/) */}
        {/* URL will be /admin/master and /admin/client */}
        <Route path="/master/*" element={<MasterProfilePage />} />
        <Route path="/client/*" element={<ClientProfilePage />} />

        {/* Default: Go to check */}
        <Route path="*" element={<SmartRouter />} />
      </Routes>
      <Toaster />
    </BrowserRouter>
  )
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AppContent />
      </QueryClientProvider>
    </ErrorBoundary>
  )
}

export default App
